import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, Upload, LogOut, CheckCircle2, Trash2, Image as ImageIcon,
  Eye, EyeOff, Images, ChevronUp, ChevronDown, BookOpen
} from 'lucide-react';
import { authApi, adminApi, imageUrl, publicApi } from '../api';
import { toast } from 'sonner';
import { isImageFile, prepareImage } from '../lib/prepareImage';

export default function Admin() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const t = sessionStorage.getItem('ksv_token');
    if (t) setLoggedIn(true);
    setChecking(false);
  }, []);

  if (checking) return <div className="pt-40 text-center text-[#5a4a3d]">Lädt …</div>;

  return loggedIn ? <Dashboard onLogout={() => setLoggedIn(false)}/> : <LoginForm onSuccess={() => setLoggedIn(true)}/>;
}

function LoginForm({ onSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setLoading(true);
    try {
      const res = await authApi.login(username, password);
      sessionStorage.setItem('ksv_token', res.token);
      onSuccess();
    } catch (er) {
      setErr(er?.response?.data?.detail || 'Anmeldung fehlgeschlagen.');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#faf6f0] pt-32 pb-24 min-h-screen">
      <div className="max-w-md mx-auto px-6">
        <div className="bg-white rounded-2xl shadow-xl border border-amber-100 p-10">
          <div className="w-16 h-16 rounded-full bg-amber-500 text-white flex items-center justify-center mx-auto mb-6"><Lock size={28}/></div>
          <h1 className="font-serif text-3xl text-[#1a1410] text-center">Admin-Login</h1>
          <p className="text-center text-[#5a4a3d] mt-2">Nur für autorisiertes Personal</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-[#3d332b]">Benutzername</span>
              <input value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus
                className="mt-1 w-full px-4 py-3 rounded-lg border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-[#faf6f0]"
                placeholder="KSV-Cantine"/>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-[#3d332b]">Passwort</span>
              <div className="relative mt-1">
                <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 pr-11 rounded-lg border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-[#faf6f0]"
                  placeholder="••••••••"/>
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a4a3d] hover:text-amber-700">
                  {showPw ? <EyeOff size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </label>
            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{err}</div>}
            <button type="submit" disabled={loading} className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#1a1410] font-semibold transition-colors">
              {loading ? 'Anmelden …' : 'Anmelden'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  const showNotice = (m) => toast.success(m);
  const logout = () => { sessionStorage.removeItem('ksv_token'); onLogout(); navigate('/'); };
  const onAuthError = (er) => {
    if (er?.response?.status === 401) { sessionStorage.removeItem('ksv_token'); onLogout(); return true; }
    return false;
  };

  return (
    <div className="bg-[#faf6f0] pt-32 pb-24 min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <span className="text-amber-600 tracking-[0.3em] text-xs uppercase">Verwaltung</span>
            <h1 className="font-serif text-4xl md:text-5xl text-[#1a1410] mt-2">Admin-Bereich</h1>
            <p className="text-[#5a4a3d] mt-2">Wochenmenü, Speisekarte, Saisonkarte und Galerie verwalten</p>
          </div>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300 text-[#3d332b] hover:bg-amber-50 transition-colors text-sm">
            <LogOut size={16}/> Abmelden
          </button>
        </div>


        <div className="space-y-8">
          <MenuSection kind="weekly-menu" title="Wochenmenü" showNotice={showNotice} onAuthError={onAuthError}/>
          <AlbumSection album="speisekarte" title="Speisekarte" icon={BookOpen} reorder showNotice={showNotice} onAuthError={onAuthError}/>
          <MenuSection kind="seasonal-menu" title="Saisonkarte" showNotice={showNotice} onAuthError={onAuthError}/>
          <AlbumSection album="gallery" title="Galerie" icon={Images} showNotice={showNotice} onAuthError={onAuthError}/>
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, extra, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-amber-100 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-amber-100 flex items-center justify-between bg-[#faf6f0]">
        <h2 className="font-serif text-xl text-[#1a1410] flex items-center gap-2"><Icon size={20} className="text-amber-500"/> {title}</h2>
        {extra}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function validFile(f) {
  if (!isImageFile(f)) return `Keine Bilddatei: ${f.name}`;
  if (f.size > 30 * 1024 * 1024) return `Datei zu groß (max. 30 MB): ${f.name}`;
  return '';
}

function useError() {
  const [err, setErrState] = useState('');
  const setErr = (m) => { setErrState(m); if (m) toast.error(m); };
  return [err, setErr];
}

function MenuSection({ kind, title, showNotice, onAuthError }) {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useError();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setMenu(await publicApi.getMenu(kind)); }
    catch { setMenu(null); }
    finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const v = validFile(f); if (v) { setErr(v); return; }
    setErr(''); setUploading(true);
    try {
      await adminApi.uploadMenu(kind, await prepareImage(f));
      showNotice(`${title} erfolgreich hochgeladen!`);
      await load();
    } catch (er) {
      if (!onAuthError(er)) setErr(errText(er));
    } finally { setUploading(false); e.target.value = ''; }
  };

  const remove = async () => {
    if (!window.confirm(`${title} wirklich entfernen?`)) return;
    try {
      await adminApi.deleteMenu(kind);
      showNotice(`${title} entfernt.`);
      await load();
    } catch (er) { if (!onAuthError(er)) setErr('Löschen fehlgeschlagen.'); }
  };

  const extra = menu && <span className="text-xs text-[#5a4a3d]">Zuletzt: {new Date(menu.uploaded_at).toLocaleString('de-DE')}</span>;

  return (
    <Card title={title} icon={ImageIcon} extra={extra} testid={`admin-section-${kind}`}>
      {loading ? (
        <div className="text-center text-[#5a4a3d] py-10">Lädt …</div>
      ) : menu ? (
        <div className="space-y-4">
          <div className="rounded-lg overflow-hidden border border-amber-100 bg-[#f3ebdd]">
            <img src={imageUrl(menu.image_url)} alt={title} data-testid={`admin-${kind}-preview`} className="w-full max-h-[600px] object-contain"/>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`admin-${kind}-replace-btn`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#1a1410] font-semibold text-sm">
              <Upload size={16}/> {uploading ? 'Lädt hoch …' : 'Neues Bild hochladen'}
            </button>
            <button onClick={remove} data-testid={`admin-${kind}-delete-btn`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-red-300 text-red-700 hover:bg-red-50 font-semibold text-sm">
              <Trash2 size={16}/> Entfernen
            </button>
          </div>
        </div>
      ) : (
        <DropButton onClick={() => fileRef.current?.click()} uploading={uploading} label={`${title}-Bild hochladen`} testid={`admin-${kind}-upload-btn`}/>
      )}
      <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid={`admin-${kind}-file-input`}/>
      {err && <ErrorBox msg={err}/>}
    </Card>
  );
}

function errText(er) {
  const d = er?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (er?.response?.status === 413) return 'Datei zu groß';
  return er?.response ? `Fehler ${er.response.status}` : 'Keine Verbindung zum Server';
}

function DropButton({ onClick, uploading, label, uploadingLabel, hint = 'JPG, PNG oder WEBP · Fotos werden automatisch verkleinert', testid }) {
  return (
    <button onClick={onClick} disabled={uploading} data-testid={testid} className="w-full border-2 border-dashed border-amber-300 rounded-xl py-14 flex flex-col items-center justify-center hover:border-amber-500 hover:bg-amber-50/50 transition-colors group disabled:opacity-60">
      <div className="w-16 h-16 rounded-full bg-amber-100 group-hover:bg-amber-500 text-amber-700 group-hover:text-white flex items-center justify-center mb-4 transition-colors">
        <Upload size={26}/>
      </div>
      <div className="font-serif text-lg text-[#1a1410]">{uploading ? (uploadingLabel || 'Lädt hoch …') : label}</div>
      <div className="text-sm text-[#5a4a3d] mt-1">{hint}</div>
    </button>
  );
}

function ErrorBox({ msg }) {
  return <div data-testid="admin-error" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{msg}</div>;
}

function AlbumSection({ album, title, icon, reorder = false, showNotice, onAuthError }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useError();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setImages(await publicApi.getAlbum(album)); }
    catch { setImages([]); }
    finally { setLoading(false); }
  }, [album]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    e.target.value = '';
    setErr(''); setUploading(true);
    let ok = 0; const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress(`Bild ${i + 1} von ${files.length} wird hochgeladen …`);
      const v = validFile(f); if (v) { failed.push(v); continue; }
      try {
        await adminApi.uploadAlbum(album, [await prepareImage(f)]);
        ok++;
      } catch (er) {
        if (onAuthError(er)) { setUploading(false); return; }
        failed.push(`${f.name}: ${errText(er)}`);
      }
    }
    setUploading(false); setProgress('');
    if (ok) showNotice(`${ok} Bild(er) zu „${title}“ hinzugefügt!`);
    if (failed.length) setErr(failed.join(' · '));
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm('Dieses Bild wirklich löschen?')) return;
    try {
      await adminApi.deleteAlbumImage(album, id);
      showNotice('Bild gelöscht.');
      setImages((imgs) => imgs.filter((i) => i.id !== id));
    } catch (er) { if (!onAuthError(er)) setErr('Löschen fehlgeschlagen.'); }
  };

  const move = async (id, direction) => {
    try { await adminApi.moveAlbumImage(album, id, direction); await load(); }
    catch (er) { if (!onAuthError(er)) setErr('Verschieben fehlgeschlagen.'); }
  };

  const extra = <span className="text-xs text-[#5a4a3d]" data-testid={`admin-${album}-count`}>{loading ? '…' : `${images.length} Bilder`}</span>;

  return (
    <Card title={title} icon={icon} extra={extra} testid={`admin-section-${album}`}>
      <DropButton onClick={() => fileRef.current?.click()} uploading={uploading} label={`Bilder zu „${title}“ hinzufügen`}
        uploadingLabel={progress} hint={reorder ? 'Mehrere Seiten gleichzeitig möglich · Reihenfolge mit den Pfeilen ändern' : 'Mehrere Bilder gleichzeitig möglich · Fotos werden automatisch verkleinert'} testid={`admin-${album}-upload-btn`}/>
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid={`admin-${album}-file-input`}/>
      {err && <ErrorBox msg={err}/>}
      {loading ? (
        <div className="text-center text-[#5a4a3d] py-10">Lädt …</div>
      ) : images.length === 0 ? (
        <div className="text-center text-[#5a4a3d] py-8">Noch keine Bilder vorhanden.</div>
      ) : (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img, idx) => (
            <div key={img.id} className={`relative group rounded-lg overflow-hidden border border-amber-100 ${reorder ? 'aspect-[3/4] bg-[#f3ebdd]' : 'aspect-square'}`} data-testid={`admin-${album}-item-${img.id}`}>
              <img src={imageUrl(img.image_url)} alt={img.title || title} className={`w-full h-full ${reorder ? 'object-contain' : 'object-cover'}`}/>
              {reorder && (
                <>
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#1a1410]/80 text-white text-xs font-semibold" data-testid={`admin-${album}-page-${idx + 1}`}>Seite {idx + 1}</span>
                  <div className="absolute bottom-2 left-2 right-2 flex justify-between">
                    <MoveBtn icon={ChevronUp} label="Nach vorne" disabled={idx === 0} onClick={() => move(img.id, -1)} testid={`admin-${album}-move-up-${img.id}`}/>
                    <MoveBtn icon={ChevronDown} label="Nach hinten" disabled={idx === images.length - 1} onClick={() => move(img.id, 1)} testid={`admin-${album}-move-down-${img.id}`}/>
                  </div>
                </>
              )}
              <button onClick={() => remove(img.id)} data-testid={`admin-${album}-delete-${img.id}`} title="Löschen"
                className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 hover:bg-red-600 hover:text-white text-red-600 flex items-center justify-center shadow-md transition-colors">
                <Trash2 size={16}/>
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function MoveBtn({ icon: Icon, label, disabled, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={disabled} title={label} data-testid={testid}
      className="w-9 h-9 rounded-full bg-white/90 hover:bg-amber-500 hover:text-white text-[#1a1410] flex items-center justify-center shadow-md transition-colors disabled:opacity-30 disabled:pointer-events-none">
      <Icon size={18} className="-rotate-90"/>
    </button>
  );
}
